import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { API, fmtError, logoutRequest, saveSession } from "@/lib/auth";

const TIER_LABELS = {
  bireysel: "Bireysel",
  kobi: "KOBİ",
  kurumsal: "Kurumsal",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const Admin = () => {
  const [status, setStatus] = useState("checking");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState(null);

  const loadSignups = useCallback(async () => {
    const [ea, us] = await Promise.all([
      axios.get(`${API}/admin/early-access`),
      axios.get(`${API}/admin/users`),
    ]);
    setData(ea.data);
    setUsers(us.data);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`)
      .then(async ({ data: me }) => {
        if (me.role !== "admin") {
          setError("Bu hesap yönetici değil. Yönetici hesabıyla giriş yapın.");
          await logoutRequest();
          setStatus("login");
          return;
        }
        setStatus("ready");
        await loadSignups();
      })
      .catch(() => setStatus("login"));
  }, [loadSignups]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data: me } = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });
      saveSession(me);
      if (me.role !== "admin") {
        await logoutRequest();
        setError("Bu hesap yönetici değil. Yönetici hesabıyla giriş yapın.");
        return;
      }
      setStatus("ready");
      await loadSignups();
      toast.success("Hoş geldin, yönetici.");
    } catch (err) {
      setError(fmtError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await logoutRequest();
    setData(null);
    setUsers(null);
    setStatus("login");
  };

  return (
    <main
      data-testid="admin-page"
      className="flex min-h-screen flex-col bg-[#07090E] text-slate-50"
    >
      <header className="glass-panel border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="admin-logo" aria-label="Akış ana sayfa">
            <Logo size={30} />
          </Link>
          <div className="flex items-center gap-4">
            {status === "ready" && (
              <button
                data-testid="admin-logout-btn"
                onClick={logout}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-red-400"
              >
                <LogOut className="h-4 w-4" /> Çıkış
              </button>
            )}
            <Link
              to="/"
              data-testid="admin-back-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-emerald-400"
            >
              <ArrowLeft className="h-4 w-4" /> Siteye dön
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-12 sm:px-6">
        {status === "checking" && (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
          </div>
        )}

        {status === "login" && (
          <div className="flex flex-1 items-center justify-center">
            <motion.form
              data-testid="admin-login-form"
              onSubmit={login}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full max-w-sm rounded-2xl border border-white/8 bg-[#0D111A] p-8"
            >
              <span className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/25 bg-emerald-500/10">
                <Lock className="h-5 w-5 text-emerald-400" />
              </span>
              <h1 className="font-display text-center text-xl font-bold text-slate-50">
                Yönetici Girişi
              </h1>
              <p className="mt-1.5 text-center text-sm text-slate-500">
                Erken erişim listesini görüntülemek için giriş yap.
              </p>
              <div className="mt-7 space-y-3">
                <input
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-posta"
                  className="w-full rounded-xl border border-white/12 bg-[#07090E] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60"
                />
                <input
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Şifre"
                  className="w-full rounded-xl border border-white/12 bg-[#07090E] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-emerald-400/60"
                />
                {error && (
                  <div
                    data-testid="admin-login-error"
                    className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300"
                  >
                    {error}
                  </div>
                )}
                <button
                  data-testid="admin-login-submit"
                  type="submit"
                  disabled={busy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Giriş Yap
                </button>
              </div>
            </motion.form>
          </div>
        )}

        {status === "ready" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
                  Yönetim
                </div>
                <h1 className="font-display mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl">
                  Erken Erişim Kayıtları
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  data-testid="admin-send-reminders-btn"
                  onClick={async (e) => {
                    const btn = e.currentTarget;
                    btn.disabled = true;
                    try {
                      const { data: res } = await axios.post(
                        `${API}/admin/send-reminders`,
                        {},
                      );
                      toast.success(
                        `Hatırlatıcı gönderildi: ${res.sent} işletme (${res.skipped} atlandı, ${res.failed} hata).`,
                      );
                    } catch (err) {
                      toast.error(fmtError(err.response?.data?.detail));
                    } finally {
                      btn.disabled = false;
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
                >
                  <Mail className="h-4 w-4" />7 Günlük Hatırlatıcı Gönder
                </button>
                <button
                  data-testid="admin-send-monthly-btn"
                  onClick={async (e) => {
                    const btn = e.currentTarget;
                    btn.disabled = true;
                    try {
                      const { data: res } = await axios.post(
                        `${API}/admin/send-monthly-reports`,
                        {},
                      );
                      toast.success(
                        `Aylık rapor (${res.period}): ${res.sent} gönderildi, ${res.skipped} atlandı, ${res.failed} hata.`,
                      );
                    } catch (err) {
                      toast.error(fmtError(err.response?.data?.detail));
                    } finally {
                      btn.disabled = false;
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300 transition-all duration-300 hover:bg-emerald-500/20 disabled:opacity-60"
                >
                  <Mail className="h-4 w-4" />
                  Aylık Raporları Gönder
                </button>
                <div
                  data-testid="admin-signup-count"
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/5 px-4 py-2 text-sm font-semibold text-emerald-300"
                >
                  <Users className="h-4 w-4" />
                  {data ? data.count : "…"} kayıt
                </div>
              </div>
            </div>

            <div className="mt-8 overflow-hidden rounded-2xl border border-white/8 bg-[#0D111A]">
              <div className="hidden grid-cols-12 gap-4 border-b border-white/8 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 sm:grid">
                <span className="col-span-5">E-posta</span>
                <span className="col-span-3">Segment</span>
                <span className="col-span-4">Kayıt Tarihi</span>
              </div>
              <ul
                data-testid="admin-signup-list"
                className="divide-y divide-white/5"
              >
                {data?.items.map((item) => (
                  <li
                    key={item.email}
                    className="grid grid-cols-1 gap-2 px-6 py-4 sm:grid-cols-12 sm:items-center sm:gap-4"
                  >
                    <span className="col-span-5 flex items-center gap-2.5 text-sm font-medium text-slate-100">
                      <Mail className="h-4 w-4 shrink-0 text-emerald-400/70" />
                      {item.email}
                    </span>
                    <span className="col-span-3">
                      <span className="inline-flex rounded-full border border-white/12 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">
                        {TIER_LABELS[item.tier] || item.tier}
                      </span>
                    </span>
                    <span className="col-span-4 text-sm text-slate-500">
                      {fmtDate(item.created_at)}
                    </span>
                  </li>
                ))}
                {data && data.items.length === 0 && (
                  <li className="px-6 py-12 text-center text-sm text-slate-600">
                    Henüz kayıt yok. İlk kayıt geldiğinde burada görünecek.
                  </li>
                )}
              </ul>
            </div>

            <div className="mt-14 flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
                  Kullanıcılar
                </div>
                <h2 className="font-display mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl">
                  Kayıtlı İşletmeler
                </h2>
              </div>
              <div
                data-testid="admin-user-count"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/5 px-4 py-2 text-sm font-semibold text-emerald-300"
              >
                <Building2 className="h-4 w-4" />
                {users ? users.count : "…"} işletme
              </div>
            </div>
            <div className="mt-8 overflow-hidden rounded-2xl border border-white/8 bg-[#0D111A]">
              <div className="hidden grid-cols-12 gap-4 border-b border-white/8 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 sm:grid">
                <span className="col-span-4">İşletme / Ad</span>
                <span className="col-span-4">E-posta</span>
                <span className="col-span-1">Giriş</span>
                <span className="col-span-3">Kayıt Tarihi</span>
              </div>
              <ul
                data-testid="admin-user-list"
                className="divide-y divide-white/5"
              >
                {users?.items.map((u) => (
                  <li
                    key={u.user_id}
                    className="grid grid-cols-1 gap-2 px-6 py-4 sm:grid-cols-12 sm:items-center sm:gap-4"
                  >
                    <span className="col-span-4 text-sm font-medium text-slate-100">
                      {u.company || u.name}
                      {u.company && (
                        <span className="ml-2 text-xs text-slate-500">
                          {u.name}
                        </span>
                      )}
                    </span>
                    <span className="col-span-4 truncate text-sm text-slate-400">
                      {u.email}
                    </span>
                    <span className="col-span-1">
                      <span className="inline-flex rounded-full border border-white/12 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-300">
                        {u.auth_provider === "google" ? "Google" : "E-posta"}
                      </span>
                    </span>
                    <span className="col-span-3 text-sm text-slate-500">
                      {fmtDate(u.created_at)}
                    </span>
                  </li>
                ))}
                {users && users.items.length === 0 && (
                  <li className="px-6 py-12 text-center text-sm text-slate-600">
                    Henüz kayıtlı işletme yok.
                  </li>
                )}
              </ul>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default Admin;
